import Region from '@/creditflowresearch/domain/Region';
import Market, { MarketName } from '@/creditflowresearch/domain/Market';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import IDeal from '@/creditflowresearch/domain/vo/IDeal';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import { addQueryParams, removeQueryParams } from '@framework/router/nextRouter';
import { mapArticleTypeToTitle } from '@/creditflowresearch/ui/components/creditFlow/articles/constants';
import normalizeWhitespace from '@framework/data-helpers/string/normalizeWhitespace';
import {
  IConfigQuery,
  ILinkParams,
} from '@/creditflowresearch/ui/components/creditFlow/articles/common/useGetLinkToAllArticles/interfaces';
import {
  linkToAllArticlesConfig,
} from '@/creditflowresearch/ui/components/creditFlow/articles/common/useGetLinkToAllArticles/linkToAllArticleConfig';
import {
  mapTypeToUrl,
} from '@/creditflowresearch/ui/components/creditFlow/articles/common/useGetLinkToAllArticles/constants';

const MARKET_DISPLAY_NAMES: Partial<Record<MarketName, string>> = {
  [MarketName.EMERGING_MARKET]: 'Emerging Markets',
};

export const formatMarketDisplayName = (name: string): string =>
  MARKET_DISPLAY_NAMES[name as MarketName] ?? name;

export const sortProductsAlphabetically = (products: (IProduct & { isActive?: boolean })[]) => products
  .filter(({ isActive }) => isActive)
  .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
  .map(({ abbreviation }) => abbreviation);

export const sortRegionsWithUsoaPriority = (regions: (IRegion & { isActive?: boolean })[]) => regions
  .filter(({ isActive }) => isActive)
  .sort((a, b) => {
    if (a.id === Region.USOA_ID) return -1;
    if (b.id === Region.USOA_ID) return 1;
    return a.abbreviation.localeCompare(b.abbreviation);
  })[0]?.abbreviation;

export const getRegionDisplayName = (region: IRegion | string) => {
  if (typeof region === 'string') {
    return region;
  }
  return region?.id === Region.USOA_ID ? region.shortDisplayName : region?.abbreviation;
};

export const getTargetProductForAbsThemes = (
  activeProducts: IProduct[],
  accessibleProducts: IProduct[],
  products: string,
  deal: IDeal,
) => {
  if (activeProducts.length > 1) {
    return sortProductsAlphabetically(accessibleProducts).toString();
  }
  return products || deal?.product?.abbreviation;
};

export const getTargetRegionForAbsThemes = (
  activeRegions: IRegion[],
  accessibleRegions: IRegion[],
  regions: string,
  deal: IDeal,
) => {
  if (activeRegions.length > 1) {
    return sortRegionsWithUsoaPriority(accessibleRegions);
  }
  return regions || deal?.country?.region?.abbreviation;
};

export const resolveLink = (params: ILinkParams): {
  product: string,
  region: string,
  path: string,
  query: IConfigQuery,
} => {
  const config = linkToAllArticlesConfig.find((c) => c.match(params));
  return {
    product: config.getProduct(params),
    region: config.getRegion(params),
    query: config.getConfiguredQuery(params),
    path: config.path,
  };
};

export const buildLink = (themeType: ThemeType, product: string, region: string, query: any, path: string): string => {
  const productName = product?.toString();
  const hasMarkets = query?.markets;

  const newQuery = addQueryParams(query, {
    ...(productName ? { products: productName } : {}),
    ...(region && !hasMarkets ? { regions: region } : {}),
  });

  const cleanQuery = removeQueryParams(newQuery, ['routeType', 'industry']);
  const pathname = `${ path }${ mapTypeToUrl[themeType] }-all`;
  const params = new URLSearchParams(cleanQuery).toString();
  return params ? `${ pathname }?${ params }` : pathname;
};

export const buildTooltip = (
  themeType: ThemeType,
  product: string,
  region: string,
  markets?: string | string[],
): string => {
  const productName = product?.toString();
  const locationName = markets ? Market.getDisplayNames(markets).map(formatMarketDisplayName).join(', ') : region;
  const tooltip = `Go to ${ locationName } ${ productName } ${ mapArticleTypeToTitle[themeType] }`;
  return normalizeWhitespace(tooltip);
};
